import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  loginUser as loginUserService,
  listUsers as listUsersService,
  createUser as createUserService,
  deleteUser as deleteUserService,
  viewUser as viewUserService,
  updateUser as updateUserService,
  logoutUser as logoutUserService,
  verifyPassword as verifyPasswordService,
  changePassword as changePasswordService,
} from "../../services/UserService";
import { AuthState, User } from "../../interfaces/user-interface";

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (
    { email, password }: { email: string; password: string },
    thunkAPI
  ) => {
    try {
      const result = await loginUserService(email, password);
      if (result) {
        const { idToken: token, currentUser } = result;
        localStorage.setItem("idToken", token);
        window.dispatchEvent(new Event("customTokenUpdated"));
        return { token, currentUser };
      } else {
        return thunkAPI.rejectWithValue("Invalid email or password");
      }
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message ?? "Login failed"
      );
    }
  }
);

export const listUsers = createAsyncThunk(
  "auth/listUsers",
  async (_, thunkAPI) => {
    const token = localStorage.getItem("idToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const users = await listUsersService();
      return users;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createUser = createAsyncThunk(
  "auth/createUser",
  async ({ data }: { data: object }, thunkAPI) => {
    const token = localStorage.getItem("idToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const message = await createUserService(data);
      return message;
    } catch (error: any) {
      if (error?.response?.data?.error?.message) {
        return thunkAPI.rejectWithValue(
          `Failed to create user: ${error?.response?.data?.error?.message}`
        );
      }
      return thunkAPI.rejectWithValue("Failed to create user");
    }
  }
);

export const deleteUser = createAsyncThunk(
  "auth/deleteUser",
  async ({ id, deletedBy }: { id: string; deletedBy: string }, thunkAPI) => {
    const token = localStorage.getItem("idToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const response = await deleteUserService(id, deletedBy);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to delete user"
      );
    }
  }
);

export const viewUser = createAsyncThunk(
  "auth/viewUser",
  async ({ id }: { id: string }, thunkAPI) => {
    const token = localStorage.getItem("idToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const response = await viewUserService(id);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to delete user"
      );
    }
  }
);

export const verifyPassword = createAsyncThunk(
  "auth/verifyPassword",
  async (
    { userId, password }: { userId: string; password: string },
    thunkAPI
  ) => {
    const token = localStorage.getItem("idToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const response = await verifyPasswordService({ userId, password });
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to verify user password"
      );
    }
  }
);

export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (
    { userId, password }: { userId: string; password: string },
    thunkAPI
  ) => {
    const token = localStorage.getItem("idToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const response = await changePasswordService({ userId, password });
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error?.message ??
          error.response?.data?.message ??
          "Failed to reset password"
      );
    }
  }
);

export const updateUser = createAsyncThunk(
  "auth/updateUser",
  async ({ id, data }: { id: string; data: object }, thunkAPI) => {
    const token = localStorage.getItem("idToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const response = await updateUserService(id, data);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to update user"
      );
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (data: object, thunkAPI) => {
    const token = localStorage.getItem("idToken");
    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }
    try {
      const response = await logoutUserService(data);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to logout user"
      );
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    idToken: null,
    status: "idle",
    error: null,
    users: [] as User[],
    selectedUser: null,
    currentUser: null,
    passwordChangeError: "",
  } as AuthState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.idToken = action.payload.token;
        state.currentUser = action.payload.currentUser;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(listUsers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(listUsers.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.users = action.payload;
        state.error = null;
      })
      .addCase(listUsers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(createUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(deleteUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.users = state.users.filter(
          (user) => user.id !== action.payload.id
        );
        state.error = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(verifyPassword.pending, (state) => {
        state.passwordChangeStatus = "loading";
      })
      .addCase(verifyPassword.fulfilled, (state, action) => {
        state.passwordChangeStatus = "succeeded";
        state.passwordChangeError = null;
      })
      .addCase(verifyPassword.rejected, (state, action) => {
        state.passwordChangeStatus = "failed";
        state.passwordChangeError = action.payload as string;
      })
      .addCase(changePassword.pending, (state) => {
        state.passwordChangeStatus = "loading";
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.passwordChangeStatus = "succeeded";
        state.passwordChangeError = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.passwordChangeStatus = "failed";
        state.passwordChangeError = action.payload as string;
      })
      .addCase(viewUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(viewUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selectedUser = action.payload;
        state.error = null;
      })
      .addCase(viewUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(updateUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selectedUser = action.payload;
        state.error = null;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      });
  },
});

export default authSlice.reducer;
